import React from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  BellOff,
  Sparkles,
} from 'lucide-react';
import { formatDate } from './contractDetailUtils';

export default function ContractDetailAlerts({
  autoRenewWindow,
  canWriteContracts,
  contract,
  daysToExp,
  daysToNoticeDate,
  dismissReminder,
  isAlreadyExpired,
  isExpiringSoon,
  markReviewed,
  noticeReminderDismissed,
  noticeWindowClosed,
  noticeWindowOpen,
  openStatusModal,
  renewalReminderDismissed,
}) {
  const rows = [];

  // Auto-created from an AI upload and not yet checked by a human. Reviewer-only
  // — it's an internal QA cue, and only legal/admin can act on it. Sits at the
  // top of the stack when shown.
  if (contract.needsReview && canWriteContracts) {
    rows.push({
      key: 'needs-review',
      tone: 'yellow',
      Icon: Sparkles,
      body: (
        <>
          <strong>Auto-created from an upload — needs review.</strong>{' '}
          The details below were extracted by AI; confirm them, then save or mark reviewed.
          {contract.possibleDuplicateOf && (
            <>
              {' '}This may be a{' '}
              <Link to={`/contracts/${contract.possibleDuplicateOf}`}>version of an existing contract</Link>.
            </>
          )}
        </>
      ),
      actions: canWriteContracts ? (
        <>
          <Link to={`/contracts/${contract.id}/edit`} className="btn ghost sm">Review</Link>
          <button type="button" className="btn ghost sm" onClick={markReviewed}>
            Mark reviewed
          </button>
        </>
      ) : null,
    });
  }

  // doc-375: the auto-renew sweep hit its term but couldn't compute the new end
  // date, so it left the contract active and flagged rather than inventing one.
  // Legal resolves it by setting a real expiration date (or recording a
  // non-renewal notice) on the Term & Renewal card — which clears the flag.
  if (contract.renewalReviewNeeded) {
    rows.push({
      key: 'renewal-review-needed',
      tone: 'yellow',
      Icon: AlertTriangle,
      body: (
        <>
          <strong>Term ended — this contract auto-renews, but its new end date needs confirming.</strong>{' '}
          The renewal term couldn't be read automatically, so no date was guessed.
          {' '}Set the expiration date{contract.expirationDate ? (
            <> (currently <span className="mono">{formatDate(contract.expirationDate)}</span>)</>
          ) : null}, or record a non-renewal notice, on the Term &amp; Renewal card.
        </>
      ),
      actions: canWriteContracts ? (
        <Link to={`/contracts/${contract.id}/edit#sec-dates`} className="btn ghost sm">
          Set end date
        </Link>
      ) : null,
    });
  }

  if (autoRenewWindow && !noticeReminderDismissed) {
    rows.push({
      key: 'auto-renew',
      tone: 'yellow',
      Icon: AlertTriangle,
      body: (
        <>
          <strong>Auto-renews in {daysToExp} day{daysToExp !== 1 ? 's' : ''}.</strong>{' '}
          {noticeWindowOpen && (
            <>
              Notice window closes{' '}
              <span className="mono">{formatDate(contract.autoRenewalNoticeDate)}</span>
              {' '}({daysToNoticeDate} day{daysToNoticeDate !== 1 ? 's' : ''} left to opt out).
            </>
          )}
          {noticeWindowClosed && (
            <>
              Notice window <strong>closed</strong>{' '}
              <span className="mono">{formatDate(contract.autoRenewalNoticeDate)}</span>
              {' '}— renewal can no longer be prevented.
            </>
          )}
          {!contract.autoRenewalNoticeDate && contract.renewalNoticeDays && (
            <>
              {contract.renewalNoticeDays}-day notice required before{' '}
              <span className="mono">{formatDate(contract.expirationDate)}</span>.
            </>
          )}
        </>
      ),
      actions: canWriteContracts && contract.autoRenewalNoticeDate ? (
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => dismissReminder('notice')}
          title="Dismiss this notice reminder. It'll come back if the notice date shifts."
        >
          Dismiss
        </button>
      ) : null,
    });
  }

  if (!autoRenewWindow && isExpiringSoon && !renewalReminderDismissed) {
    rows.push({
      key: 'expiring',
      tone: 'yellow',
      Icon: AlertCircle,
      body: (
        <>
          <strong>Expires in {daysToExp} day{daysToExp !== 1 ? 's' : ''}</strong>
          <span className="muted"> · {formatDate(contract.expirationDate)}</span>
        </>
      ),
      actions: canWriteContracts ? (
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => dismissReminder('renewal')}
          title="Dismiss this renewal reminder. It'll come back if the expiration date shifts."
        >
          Dismiss
        </button>
      ) : null,
    });
  }

  if (isAlreadyExpired && !renewalReminderDismissed) {
    rows.push({
      key: 'expired',
      tone: 'red',
      Icon: AlertCircle,
      body: (
        <>
          <strong>Expired {Math.abs(daysToExp)} day{Math.abs(daysToExp) !== 1 ? 's' : ''} ago</strong>
          <span className="muted"> · {formatDate(contract.expirationDate)}</span>
        </>
      ),
      actions: canWriteContracts ? (
        <>
          <button onClick={openStatusModal} className="btn ghost sm">Update status</button>
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => dismissReminder('renewal')}
          >
            Dismiss
          </button>
        </>
      ) : null,
    });
  }

  if (renewalReminderDismissed) {
    rows.push({
      key: 'renewal-dismissed',
      tone: 'muted',
      Icon: BellOff,
      body: (
        <>
          Renewal reminder dismissed for{' '}
          <span className="mono">{formatDate(contract.expirationDate)}</span>.
        </>
      ),
      actions: canWriteContracts ? (
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => dismissReminder('renewal', true)}
        >
          Re-enable
        </button>
      ) : null,
    });
  }

  if (noticeReminderDismissed) {
    rows.push({
      key: 'notice-dismissed',
      tone: 'muted',
      Icon: BellOff,
      body: (
        <>
          Notice-window reminder dismissed for{' '}
          <span className="mono">{formatDate(contract.autoRenewalNoticeDate)}</span>.
        </>
      ),
      actions: canWriteContracts ? (
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => dismissReminder('notice', true)}
        >
          Re-enable
        </button>
      ) : null,
    });
  }

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="alert-stack">
      {rows.map((row) => (
        <div key={row.key} className={`alert-row alert-row--${row.tone}`}>
          <span className="alert-icon"><row.Icon className="w-4 h-4" /></span>
          <div className="alert-body">{row.body}</div>
          {row.actions && <div className="alert-actions">{row.actions}</div>}
        </div>
      ))}
    </div>
  );
}
